import { dedupeThreadMessages, type ThreadMessage } from "@/domains/offline/chat/chatMessageDedupe";
import type { ChatMessageRecord, ConversationRecord } from "@/domains/offline/db/schema";

export type CachedConversation = Omit<ConversationRecord, "updatedAt">;
export type CachedChatMessage = ChatMessageRecord & { pending?: boolean };

const transientConversations = new Map<string, CachedConversation>();
const transientMessages = new Map<string, CachedChatMessage[]>();
const transientAvailability = new Map<string, { messagingAllowed: boolean; message?: string | null }>();

export async function persistConversations(rows: CachedConversation[]): Promise<void> {
  transientConversations.clear();
  for (const row of rows) {
    transientConversations.set(row.id, row);
  }
}

export async function getCachedConversations(): Promise<CachedConversation[]> {
  return [...transientConversations.values()];
}

export async function upsertConversation(row: CachedConversation): Promise<void> {
  transientConversations.set(row.id, row);
}

export async function persistConversationMessages(
  conversationId: string,
  messages: CachedChatMessage[],
): Promise<void> {
  transientMessages.set(conversationId, [...messages]);
}

export async function getCachedConversationMessages(conversationId: string): Promise<CachedChatMessage[]> {
  return transientMessages.get(conversationId) ?? [];
}

/** Keep server history in memory while preserving local pending bubbles for the current session. */
export async function persistServerConversationMessages(
  conversationId: string,
  serverMessages: CachedChatMessage[],
): Promise<void> {
  const existing = transientMessages.get(conversationId) ?? [];
  const pendingOnly = existing.filter((m) => m.id.startsWith("pending-"));
  const byId = new Map<string, CachedChatMessage>();

  for (const message of serverMessages) {
    byId.set(message.id, message);
  }

  for (const message of pendingOnly) {
    byId.set(message.id, message);
  }

  const merged = [...byId.values()].map((m) => ({
    ...m,
    conversationId: m.conversationId ?? conversationId,
  }));
  transientMessages.set(conversationId, dedupeThreadMessages(merged) as CachedChatMessage[]);
}

export async function replacePendingCachedMessage(
  conversationId: string,
  pendingId: string,
  msg: CachedChatMessage,
): Promise<void> {
  const existing = transientMessages.get(conversationId) ?? [];
  const withoutPending = existing.filter((message) => message.id !== pendingId);
  const fingerprint = `${msg.senderId}|${msg.content.trim()}|${msg.createdAt}`;
  const withoutDupes = withoutPending.filter(
    (message) => `${message.senderId}|${message.content.trim()}|${message.createdAt}` !== fingerprint,
  );
  const merged = [...withoutDupes, { ...msg, conversationId: msg.conversationId ?? conversationId }];
  transientMessages.set(conversationId, dedupeThreadMessages(merged) as CachedChatMessage[]);
}

export async function appendCachedMessage(conversationId: string, msg: CachedChatMessage): Promise<void> {
  const existing = transientMessages.get(conversationId) ?? [];
  if (existing.some((message) => message.id === msg.id)) {
    return;
  }

  transientMessages.set(
    conversationId,
    [...existing, msg].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
  );
}

export async function persistChatAvailability(
  therapistId: string,
  messagingAllowed: boolean,
  message?: string | null,
): Promise<void> {
  transientAvailability.set(therapistId, { messagingAllowed, message });
}

export async function getCachedChatAvailability(therapistId: string): Promise<{
  messagingAllowed: boolean;
  message?: string | null;
} | null> {
  return transientAvailability.get(therapistId) ?? null;
}

export async function mapTempConversationId(tempId: string, serverId: string): Promise<void> {
  const conversation = transientConversations.get(tempId);
  if (conversation) {
    transientConversations.delete(tempId);
    transientConversations.set(serverId, { ...conversation, id: serverId });
  }

  const messages = transientMessages.get(tempId);
  if (messages) {
    transientMessages.delete(tempId);
    transientMessages.set(
      serverId,
      messages.map((message) => ({ ...message, conversationId: serverId })),
    );
  }
}

export function resetTransientChatCache(): void {
  transientConversations.clear();
  transientMessages.clear();
  transientAvailability.clear();
}
